import { Formik } from 'formik';
import * as Yup from 'yup';
import { Form, Cascader, Button, Row, Col } from 'antd';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Cpu, Edit3, Trash2, GitMerge } from 'lucide-react';
import { get_machine_category_with_groups, get_operation_machine_setup, get_machine_spec_values } from '@/utils/api';
import useAppSetting from '@/hooks/useAppSetting';

const singleSchema = Yup.object().shape({
  machine_type_id: Yup.mixed().nullable().required('Machine category is required'),
  machine_group_id: Yup.mixed().nullable().required('Machine group is required'),
});
const multiSchema = Yup.object().shape({
  machine_type_id: Yup.mixed().nullable().required('Machine category is required'),
  machine_group_id: Yup.mixed().nullable().required('Machine group is required'),
});

export default function MachineSetup({
  fromView,
  process_id,
  recipe_id,
  previous_operations = [],
  machine_multi_select_enabled = false,
  machineRule = null,
  onSubmit = (values) => {
    console.log('Form submitted:', values);
  },
  values = {
    machine_type_id: null,
    machine_group_id: null,
    machine_id: null,
    machine_ids: [],
    machine_type: null,
    execution_rules: null,
  },
}) {
  const rulesEnabled = useAppSetting('Rules');

  const VALIDATION_RULES = [
    {
      key: 'line_clearance_verification',
      title: 'Line Clearance Verification',
      desc: 'Validate line clearance before equipment binding',
      def: 'REQUIRED',
    },
    {
      key: 'cleaning_status_verification',
      title: 'Cleaning Status Verification',
      desc: 'Ensure equipment cleaning released before operation',
      def: 'VERIFIED',
    },
    {
      key: 'calibration_verification',
      title: 'Calibration Verification',
      desc: 'Validate active calibration status',
      def: 'OPTIONAL',
    },
  ];
  const STATUS_CYCLE = ['REQUIRED', 'VERIFIED', 'OPTIONAL', 'DISABLED'];

  const effStatus = (vals, r) => {
    const chosen = (vals.execution_rules || {})[r.key];
    if (chosen !== undefined && chosen !== null) return chosen;
    return machineRule?.[r.key] || r.def;
  };
  const resolveExecutionRules = (vals) =>
    VALIDATION_RULES.reduce((acc, r) => {
      acc[r.key] = effStatus(vals, r);
      return acc;
    }, {});

  const handleResolvedSubmit = (vals, helpers) => {
    onSubmit(
      {
        ...vals,
        machine_ids: selectedMachineIds,
        machine_ids_csv: selectedMachineIds.join(','),
        execution_rules: resolveExecutionRules(vals),
      },
      helpers
    );
  };

  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);
  // [categoryId, groupId] — drives the 2-level cascader
  const [cascaderValue, setCascaderValue] = useState(null);
  // machines available under the selected group
  const [availableMachines, setAvailableMachines] = useState([]);
  // machine_ids currently checked (default: all)
  const [selectedMachineIds, setSelectedMachineIds] = useState([]);
  // Carry Over (copy dependent operation's machine setup) loading state
  const [carryLoading, setCarryLoading] = useState(false);
  // { [machine_id]: { value, uom_name, name } | null } — the max_limit-tagged
  // capacity spec (Usable Capacity/Volume) for each selected machine, null
  // while still loading or once confirmed there isn't one for that machine.
  const [machineCapacities, setMachineCapacities] = useState({});

  // Material-fill ceiling for each selected machine — whichever capacity
  // spec is tagged validation_role="max_limit" on its type (set once,
  // centrally, under Machine -> Machine Specs -> Capacity; never guessed
  // here). Refetches whenever the selection changes; a machine whose type
  // has no max_limit spec configured simply shows nothing for it.
  useEffect(() => {
    if (!selectedMachineIds.length) {
      setMachineCapacities({});
      return;
    }
    selectedMachineIds.forEach((machineId) => {
      get_machine_spec_values(machineId, (res) => {
        const rows = res?.data?.data || [];
        const ceiling = rows.find((r) => r.validation_role === 'max_limit' && r.value != null);
        setMachineCapacities((prev) => ({ ...prev, [machineId]: ceiling || null }));
      }, 'CAPACITY');
    });
  }, [selectedMachineIds]);

  // Apply a fetched machine setup into the form + local UI state
  const applyMachineSetup = (setup, setFieldValue) => {
    const catId = setup.machine_type_id;
    const grpId = setup.machine_group_id;
    if (!catId || !grpId) return false;
    const machines = getMachinesForSelection(catId, grpId, machineTypeWithGroups);
    const savedIds =
      Array.isArray(setup.machine_ids) && setup.machine_ids.length
        ? setup.machine_ids.map(Number)
        : machines.map((m) => m.machine_id);
    const groupMachineIds = machines.map((m) => m.machine_id);
    const allSelected = savedIds.length === groupMachineIds.length && groupMachineIds.length > 0;
    const leafVal = allSelected ? '__all__' : savedIds[0] ?? groupMachineIds[0] ?? '__all__';

    setAvailableMachines(machines);
    setSelectedMachineIds(savedIds);
    setCascaderValue([catId, grpId, leafVal]);
    setFieldValue('machine_type_id', catId);
    setFieldValue('machine_group_id', grpId);
    setFieldValue('machine_type', setup.machine_type || '');
    setFieldValue('machine_ids', savedIds);
    setFieldValue('machine_id', savedIds[0] ?? null);
    return true;
  };

  const opLabel = (op) => op?.operation_name || `Operation ${op?.operation_number}`;

  // Carry Over from ONE specific dependent operation.
  const carryFromOperation = (op, setFieldValue) => {
    if (!recipe_id) {
      toast.warning('Recipe context is missing; cannot carry over.');
      return;
    }
    if (!op || op.operation_number == null) return;
    setCarryLoading(true);
    get_operation_machine_setup({ recipe_id, operation_number: op.operation_number }, (res) => {
      setCarryLoading(false);
      if (res?.data?.status === 'success' && res?.data?.data) {
        const applied = applyMachineSetup(res.data.data, setFieldValue);
        if (applied) {
          toast.success(`Machine carried over from "${opLabel(op)}".`);
        } else {
          toast.warning("The dependent operation's machine setup is incomplete.");
        }
      } else {
        toast.warning(
          res?.data?.message || `"${opLabel(op)}" does not have a machine setup configured.`
        );
      }
    });
  };

  // Carry Over (direct click): use the dependent operation automatically.
  const handleCarryOver = (setFieldValue) => {
    const depOps = (previous_operations || []).filter((op) => op?.operation_number != null);
    if (!depOps.length) {
      toast.warning('This operation has no dependent operation to carry the machine setup from.');
      return;
    }
    // Use the first dependent operation.
    carryFromOperation(depOps[0], setFieldValue);
  };

  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    }, process_id);
  }, [process_id]);

  // Resolve machines for a given [categoryId, groupId] selection
  const getMachinesForSelection = (catId, grpId, data) => {
    const cat = data.find((c) => String(c.machine_category_id) === String(catId));
    if (!cat) return [];
    const grp = cat.machine_groups?.find((g) => String(g.machine_group_id) === String(grpId));
    return grp?.machines || [];
  };

  // On load / edit: restore cascader + machines from saved values
  useEffect(() => {
    const catId = values.machine_type_id;
    const grpId = values.machine_group_id;
    if (!machineTypeWithGroups.length) return;
    if (catId && grpId) {
      const machines = getMachinesForSelection(catId, grpId, machineTypeWithGroups);
      setAvailableMachines(machines);
      // Parse saved ids — support both array and CSV string
      let saved = [];
      if (Array.isArray(values.machine_ids) && values.machine_ids.length) {
        saved = values.machine_ids.map(Number);
      } else if (values.machine_ids_csv) {
        saved = String(values.machine_ids_csv).split(',').map(Number).filter(Boolean);
      }
      // Default to all machines if nothing saved
      if (!saved.length) saved = machines.map((m) => m.machine_id);
      setSelectedMachineIds(saved);
      const groupMachineIds = machines.map((m) => m.machine_id);
      const allSelected = saved.length === groupMachineIds.length && groupMachineIds.length > 0;
      const leafVal = allSelected ? '__all__' : saved[0] ?? groupMachineIds[0] ?? '__all__';
      setCascaderValue([catId, grpId, leafVal]);
    } else {
      setCascaderValue(null);
      setAvailableMachines([]);
      setSelectedMachineIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.machine_type_id,
    values.machine_group_id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(values.machine_ids),
    values.machine_ids_csv,
    machineTypeWithGroups.length,
  ]);

  // 3-level cascader: category → group → machine (plain string labels only)
  // Machine counts on the Category/Group labels — without them, a category with several
  // empty placeholder groups (shown regardless of the process's area — see
  // get_machine_category_with_groups) reads identically to one with real, located equipment,
  // and there was no way to tell which was which without drilling in.
  const cascaderOptions = machineTypeWithGroups.map((cat) => {
    const catMachineCount = (cat.machine_groups || []).reduce((sum, g) => sum + (g.machines || []).length, 0);
    return {
      label: `${cat.machine_category_name} (${catMachineCount})`,
      value: cat.machine_category_id,
      children: (cat.machine_groups || []).map((g) => {
        const machines = g.machines || [];
        return {
          label: `${g.machine_group_name} (${machines.length})`,
          value: g.machine_group_id,
          children: [
            {
              label: `All Machines (${machines.length})`,
              value: '__all__',
              _allIds: machines.map((m) => m.machine_id),
              _groupId: g.machine_group_id,
              _catId: cat.machine_category_id,
              _catLabel: cat.machine_category_name,
              _grpLabel: g.machine_group_name,
            },
            ...machines.map((m) => ({
              label: m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name,
              value: m.machine_id,
              _machineId: m.machine_id,
              _machineName: m.machine_name,
              _groupId: g.machine_group_id,
              _catId: cat.machine_category_id,
              _catLabel: cat.machine_category_name,
              _grpLabel: g.machine_group_name,
              _allIds: machines.map((x) => x.machine_id),
            })),
          ],
        };
      }),
    };
  });

  return (
    <Formik
      initialValues={values}
      validationSchema={machine_multi_select_enabled ? multiSchema : singleSchema}
      onSubmit={handleResolvedSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched, handleSubmit }) => (
        <Form layout="vertical" onFinish={handleSubmit}>
          <div className="mb-3">
            <p className="text-xs text-gray-400 dark:text-slate-400 m-0">
              Configure operational equipment context and execution validation
            </p>
          </div>

          <div className="flex gap-4 items-start">
            <div className={`min-w-0 ${rulesEnabled ? 'w-[calc(75%-8px)]' : 'w-full'}`}>
              {/* ── 3-level Cascader: Category → Group → Machine — with inline Carry Over ── */}
              <Form.Item
                label="Machine"
                validateStatus={
                  (touched.machine_type_id && errors.machine_type_id) ||
                  (touched.machine_group_id && errors.machine_group_id)
                    ? 'error'
                    : ''
                }
                help={
                  (touched.machine_type_id && errors.machine_type_id) ||
                  (touched.machine_group_id && errors.machine_group_id)
                }
              >
                <div className="flex items-center gap-2">
                  <Cascader
                    showSearch
                    allowClear
                    placeholder="Select Category → Group → Machine"
                    value={cascaderValue || undefined}
                    style={{ flex: 1, minWidth: 0 }}
                    popupClassName="cascader-wide-dropdown"
                    dropdownMenuColumnStyle={{ minWidth: 200 }}
                    expandTrigger="hover"
                    options={cascaderOptions}
                    displayRender={(labels) => labels.join(' / ')}
                    onChange={(val, selectedOptions) => {
                      if (!val || val.length === 0) {
                        setCascaderValue(null);
                        setAvailableMachines([]);
                        setSelectedMachineIds([]);
                        setFieldValue('machine_type_id', null);
                        setFieldValue('machine_group_id', null);
                        setFieldValue('machine_type', null);
                        setFieldValue('machine_ids', []);
                        setFieldValue('machine_id', null);
                        return;
                      }

                      const leafOpt = selectedOptions?.[selectedOptions.length - 1];
                      const catId = leafOpt?._catId ?? val[0];
                      const grpId = leafOpt?._groupId ?? val[1];
                      const catLabel = leafOpt?._catLabel ?? selectedOptions?.[0]?.label ?? '';
                      const grpLabel = leafOpt?._grpLabel ?? selectedOptions?.[1]?.label ?? '';
                      const allIds = leafOpt?._allIds || [];

                      // Determine which machine IDs to select
                      let pickedIds;
                      if (leafOpt?.value === '__all__') {
                        // "All Machines" shortcut — select every machine in the group
                        pickedIds = allIds;
                      } else if (leafOpt?._machineId !== undefined) {
                        // Single machine selected
                        pickedIds = [leafOpt._machineId];
                      } else {
                        pickedIds = allIds;
                      }

                      // Store cascader display value as [catId, grpId, leafValue]
                      setCascaderValue(val);

                      // Load full machine list for the group so the summary panel can display it
                      const machines = getMachinesForSelection(catId, grpId, machineTypeWithGroups);
                      setAvailableMachines(machines);
                      setSelectedMachineIds(pickedIds);

                      const label = `${catLabel} / ${grpLabel}`;
                      setFieldValue('machine_type_id', catId);
                      setFieldValue('machine_group_id', grpId);
                      setFieldValue('machine_type', label);
                      setFieldValue('machine_ids', pickedIds);
                      setFieldValue('machine_id', pickedIds[0] ?? null);
                    }}
                  />

                  {/* Inline Carry Over — copy the machine from the dependent operation */}
                  {!fromView && (
                    <Button
                      type="primary"
                      icon={<GitMerge className="w-3.5 h-3.5" />}
                      loading={carryLoading}
                      onClick={() => handleCarryOver(setFieldValue)}
                      className="flex items-center gap-1 !font-semibold !shadow-md hover:!scale-105 transition-transform"
                      style={{
                        flexShrink: 0,
                        background: '#204448',
                        borderColor: '#204448',
                        color: '#fff',
                      }}
                      title="Copy the machine from the dependent operation"
                    >
                      Carry Over
                    </Button>
                  )}
                </div>
              </Form.Item>

              {/* ── Machine selection panel (shown after category/group chosen) ── */}
              {availableMachines.length > 0 && (
                <Form.Item
                  label={`Machines (${selectedMachineIds.length} / ${availableMachines.length} selected)`}
                >
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, maxWidth: '100%' }}>
                    {/* Header row with Select All / Deselect All — stays fixed above the scrollable list */}
                    {!fromView && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderBottom: '1px solid #f0f0f0',
                          background: '#fafafa',
                          borderRadius: '6px 6px 0 0',
                        }}
                      >
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#374151',
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{
                              width: 15,
                              height: 15,
                              cursor: 'pointer',
                              accentColor: '#204448',
                            }}
                            checked={
                              selectedMachineIds.length === availableMachines.length &&
                              availableMachines.length > 0
                            }
                            ref={(el) => {
                              if (el)
                                el.indeterminate =
                                  selectedMachineIds.length > 0 &&
                                  selectedMachineIds.length < availableMachines.length;
                            }}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? availableMachines.map((m) => m.machine_id)
                                : [];
                              setSelectedMachineIds(next);
                              setFieldValue('machine_ids', next);
                              setFieldValue('machine_id', next[0] ?? null);
                            }}
                          />
                          {selectedMachineIds.length === availableMachines.length
                            ? 'Deselect All'
                            : 'Select All'}
                        </label>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          {selectedMachineIds.length} selected
                        </span>
                      </div>
                    )}
                    {/* Machine rows — scrolls internally past ~7 rows instead of growing
                        the panel indefinitely and pushing the rest of the form down. */}
                    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {availableMachines.map((m, idx) => {
                        const checked = selectedMachineIds.includes(m.machine_id);
                        return (
                          <label
                            key={m.machine_id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '9px 12px',
                              borderBottom:
                                idx < availableMachines.length - 1 ? '1px solid #f0f0f0' : 'none',
                              background: checked ? '#f6fffc' : '#fff',
                              cursor: fromView ? 'default' : 'pointer',
                              userSelect: 'none',
                              transition: 'background 0.15s',
                            }}
                          >
                            <input
                              type="checkbox"
                              disabled={fromView}
                              style={{
                                width: 15,
                                height: 15,
                                cursor: fromView ? 'default' : 'pointer',
                                accentColor: '#204448',
                                flexShrink: 0,
                              }}
                              checked={checked}
                              onChange={() => {
                                const next = checked
                                  ? selectedMachineIds.filter((id) => id !== m.machine_id)
                                  : [...selectedMachineIds, m.machine_id];
                                setSelectedMachineIds(next);
                                setFieldValue('machine_ids', next);
                                setFieldValue('machine_id', next[0] ?? null);
                              }}
                            />
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: checked ? 600 : 400,
                                color: checked ? '#204448' : '#374151',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {m.machine_name}
                            </span>
                            {m.machine_code && (
                              <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
                                {m.machine_code}
                              </span>
                            )}
                            {machineCapacities[m.machine_id] && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: '#204448',
                                  flexShrink: 0,
                                  marginLeft: 10,
                                }}
                              >
                                {machineCapacities[m.machine_id].name}:{' '}
                                {machineCapacities[m.machine_id].value}
                                {machineCapacities[m.machine_id].uom_name
                                  ? ` ${machineCapacities[m.machine_id].uom_name}`
                                  : ''}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </Form.Item>
              )}

              {/* ── EXECUTION MODE (from rule; highlights the configured mode) ── */}
              <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mt-4 mb-2">
                Execution Mode
              </p>
              <Row gutter={12}>
                {[
                  {
                    title: 'Manual',
                    desc: 'Operator manually executes and confirms equipment activities.',
                  },
                  {
                    title: 'PLC Assisted',
                    desc: 'Equipment state and runtime validations integrated with PLC.',
                  },
                  {
                    title: 'SCADA Connected',
                    desc: 'Real-time equipment and process synchronization enabled.',
                  },
                ]
                  .map((m) => ({
                    ...m,
                    active: (machineRule?.execution_mode || 'Manual') === m.title,
                  }))
                  .map((m) => (
                    <Col xs={24} md={8} key={m.title}>
                      <div
                        className="rounded-lg border h-full"
                        style={{
                          borderColor: m.active ? '#204448' : '#e5e7eb',
                          background: m.active ? '#f0f7f6' : '#fff',
                          borderWidth: m.active ? 2 : 1,
                          padding: '12px 14px',
                        }}
                      >
                        <p
                          className="font-semibold text-sm m-0"
                          style={{ color: m.active ? '#204448' : '#374151' }}
                        >
                          {m.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 m-0 mt-1">{m.desc}</p>
                      </div>
                    </Col>
                  ))}
              </Row>

              {/* ── EQUIPMENT VALIDATION RULES — hidden when Rules app-setting is off ── */}
              {rulesEnabled && (
                <>
                  <p className="text-xs font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider mt-4 mb-2">
                    Equipment Validation Rules
                  </p>
                  {VALIDATION_RULES.map((r) => {
                    const status = effStatus(values, r);
                    const disabled = status === 'DISABLED';
                    // Badge color by status; OPTIONAL is amber, DISABLED grey, else green.
                    const palette =
                      status === 'OPTIONAL'
                        ? { color: '#d97706', bg: '#fef3c7' }
                        : disabled
                        ? { color: '#6b7280', bg: '#f3f4f6' }
                        : { color: '#16a34a', bg: '#dcfce7' };
                    return (
                      <div
                        key={r.title}
                        className="rounded-lg border flex items-center justify-between mb-2"
                        style={{
                          borderColor: '#e5e7eb',
                          padding: '10px 14px',
                          opacity: disabled ? 0.6 : 1,
                        }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              flexShrink: 0,
                              background: disabled ? '#f3f4f6' : '#dcfce7',
                              color: disabled ? '#9ca3af' : '#16a34a',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            ✓
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-gray-800 dark:text-slate-200 m-0">{r.title}</p>
                            <p className="text-xs text-gray-500 dark:text-slate-400 m-0">{r.desc}</p>
                          </div>
                        </div>
                        {/* Click to cycle status — overrides the rule default for this item */}
                        <span
                          onClick={() => {
                            const next =
                              STATUS_CYCLE[
                                (STATUS_CYCLE.indexOf(status) + 1) % STATUS_CYCLE.length
                              ];
                            setFieldValue('execution_rules', {
                              ...(values.execution_rules || {}),
                              [r.key]: next,
                            });
                          }}
                          title="Click to change status"
                          style={{
                            background: palette.bg,
                            color: palette.color,
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 10px',
                            borderRadius: 10,
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        >
                          {status}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* ===== RIGHT: summary panels — only when rulesEnabled ===== */}
            {rulesEnabled && (
              <div className="w-[25%] min-w-0 flex-shrink-0">
                {/* STATIC display-only — Equipment Summary */}
                <div
                  className="rounded-lg border mb-3"
                  style={{ borderColor: '#e5e7eb', padding: '12px 14px' }}
                >
                  <p className="font-semibold text-sm text-gray-800 dark:text-slate-200 m-0 mb-2">Equipment Summary</p>
                  <span
                    style={{
                      background: '#dcfce7',
                      color: '#16a34a',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 6,
                    }}
                  >
                    ACTIVE EQUIPMENT
                  </span>
                  <div className="mt-2">
                    {[
                      ['Equipment ID', 'RMG-01'],
                      ['Equipment Type', 'Granulator'],
                      ['Area', 'From Batch Context'],
                      ['Production Line', 'From Batch Context'],
                      ['Current Status', 'Available'],
                      ['Cleaning Status', 'Released'],
                      ['Last Calibration', '12-May-2026'],
                    ].map(([k, v]) => (
                      <div
                        key={k}
                        className="flex items-center justify-between"
                        style={{ padding: '3px 0' }}
                      >
                        <span className="text-xs text-gray-400 dark:text-slate-400">{k}</span>
                        <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Runtime Execution Flow */}
                <div
                  className="rounded-lg border"
                  style={{ borderColor: '#e5e7eb', padding: '12px 14px' }}
                >
                  <p className="font-semibold text-sm text-gray-800 dark:text-slate-200 m-0 mb-2">
                    Runtime Execution Flow
                  </p>
                  {[
                    'Load batch execution context',
                    'Select operational equipment',
                    'Verify equipment readiness and cleaning status',
                    'Bind equipment to operation execution',
                  ].map((label, i) => (
                    <div
                      key={label}
                      className="flex items-start gap-2"
                      style={{ padding: '4px 0' }}
                    >
                      <span
                        style={{
                          background: '#204448',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-slate-300">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Row>
            <Col span={24}>
              <Button type="primary" htmlType="submit" block className="mt-2">
                Save Machine Setup
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
}

export const MachineSetupCard = ({
  item,
  fromView,
  onEdit,
  onDelete,
  machineTypeWithGroups = [],
}) => {
  const { machine_type, machine_type_id, machine_group_id, machine_ids, machine_ids_csv } = item;

  // Resolve IDs from array or CSV fallback
  const resolvedIds =
    Array.isArray(machine_ids) && machine_ids.length > 0
      ? machine_ids.map(Number)
      : machine_ids_csv
      ? String(machine_ids_csv).split(',').map(Number).filter(Boolean)
      : [];

  // Resolve the category/group label
  const resolveGroupLabel = (catId, grpId) => {
    const cat = machineTypeWithGroups.find((c) => String(c.machine_category_id) === String(catId));
    if (!cat) return machine_type || (catId ? `ID:${catId}` : 'Machine Setup');
    const grp = cat.machine_groups?.find((g) => String(g.machine_group_id) === String(grpId));
    return grp
      ? `${cat.machine_category_name} / ${grp.machine_group_name}`
      : cat.machine_category_name;
  };

  // Resolve individual machine names from IDs
  const resolveMachineNames = (catId, grpId, ids) => {
    if (!ids || ids.length === 0) return [];
    const cat = machineTypeWithGroups.find((c) => String(c.machine_category_id) === String(catId));
    if (!cat) return ids.map((id) => `ID:${id}`);
    const grp = cat.machine_groups?.find((g) => String(g.machine_group_id) === String(grpId));
    const machines = grp?.machines || [];
    return ids.map((id) => {
      const m = machines.find((x) => String(x.machine_id) === String(id));
      return m
        ? m.machine_code
          ? `${m.machine_name} (${m.machine_code})`
          : m.machine_name
        : `ID:${id}`;
    });
  };

  const groupLabel = resolveGroupLabel(machine_type_id, machine_group_id);
  const machineNames = resolveMachineNames(machine_type_id, machine_group_id, resolvedIds);

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md transition p-3 text-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="p-1.5 bg-teal-100 rounded-md flex-shrink-0">
            <Cpu className="w-4 h-4 text-teal-600" />
          </div>
          <p
            className="font-semibold text-gray-800 dark:text-slate-200 leading-tight min-w-0 truncate"
            title={groupLabel}
          >
            {groupLabel}
          </p>
        </div>

        {/* Actions */}
        {!fromView && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
              onClick={() => onEdit()}
              title="Edit"
            >
              <Edit3 className="w-4 h-4 text-blue-600" />
            </button>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
              onClick={() => onDelete()}
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Selected machines */}
      {machineNames.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 dark:text-slate-400 mb-1">Selected Machines ({machineNames.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {machineNames.map((name, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-md text-xs bg-teal-50 text-teal-700 border border-teal-200 truncate max-w-full"
                title={name}
              >
                {i + 1}. {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
